/**
 * The stickers.
 *
 * A club orders cart stickers once and puts them on in March. The code
 * on one names a PLACE — cart 14, the tenth tee — and never a person
 * (GOLF-21), which is the whole reason a sticker printed in March is
 * still right in October and why nothing here has to be reprinted daily.
 *
 * The screen's real job is producing something to print. A QR that only
 * exists inside a web page is a QR nobody can stick on a cart, so the
 * sheet below is laid out for paper: a grid of codes with the label and
 * the code in readable type beneath each one, because a faded sticker
 * gets read out over the phone and the alphabet was chosen so that can
 * work (no 0/O, no 1/I/l, no u).
 */

import { useMemo, useState } from 'react';
import qrcode from 'qrcode-generator';

import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from './ui.tsx';
import { date as fmtDate } from '../lib/format.ts';

export interface ScanPoint {
  scan_point_id: string;
  code: string;
  label: string;
  course_id: string;
  course_name: string;
  starting_hole: number;
  is_active: boolean;
  scans: number;
  last_scan_at: string | null;
  note: string | null;
}

/**
 * A QR as an inline SVG.
 *
 * SVG rather than a canvas or a PNG for one reason: it is what prints. A
 * canvas prints at screen resolution and comes out of a laser printer
 * fuzzy at the edges, which is exactly the failure that makes a phone
 * camera hunt for two seconds in bright sun.
 *
 * Error correction 'M' — about fifteen per cent recoverable, which is
 * what survives a scuffed sticker on a cart that lives outside.
 */
function QR({ text, size = 132 }: { text: string; size?: number }) {
  const path = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    const parts: string[] = [];
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (qr.isDark(r, c)) parts.push(`M${c} ${r}h1v1h-1z`);
      }
    }
    return { d: parts.join(''), n };
  }, [text]);

  return (
    <svg
      className="sticker__qr"
      width={size}
      height={size}
      viewBox={`-1 -1 ${path.n + 2} ${path.n + 2}`}
      role="img"
      aria-label={`QR code for ${text}`}
    >
      <rect x={-1} y={-1} width={path.n + 2} height={path.n + 2} fill="#fff" />
      <path d={path.d} fill="#000" />
    </svg>
  );
}

export function Stickers({ canManage }: { canManage: boolean }) {
  const [reload, setReload] = useState(0);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [adding, setAdding] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [f, setF] = useState({ label: 'Cart', starting_hole: '1', count: '1' });

  const q = useApi<{ scan_points: ScanPoint[] }>(
    `/v1/golf/scan-points?include_retired=${includeRetired}`, [reload, includeRetired]);

  const bump = () => setReload((n) => n + 1);

  const create = useMutation(async () => {
    await api('/v1/golf/scan-points', {
      method: 'POST',
      body: {
        label: f.label.trim(),
        starting_hole: Number(f.starting_hole) || 1,
        count: Number(f.count) || 1,
      },
    });
    setAdding(false);
    setNote('Made. Print the sheet and stick them on.');
    bump();
  });

  const retire = useMutation(async (v: { id: string; active: boolean }) => {
    await api(`/v1/golf/scan-points/${v.id}`, {
      method: 'PATCH', body: { is_active: v.active },
    });
    setNote(v.active
      ? 'Back in use.'
      : 'Retired. Scanning it now says the code is not in use.');
    bump();
  });

  const rows = q.data?.scan_points ?? [];
  const origin = typeof window === 'undefined' ? '' : window.location.origin;

  return (
    <>
      <Card
        title="Stickers on the Course"
        actions={
          <div className="actions">
            <button className="secondary small"
              onClick={() => setIncludeRetired(!includeRetired)}>
              {includeRetired ? 'Only in use' : 'Show retired'}
            </button>
            {rows.length > 0 && (
              <button className="secondary small" onClick={() => setSheet(!sheet)}>
                {sheet ? 'Hide the sheet' : 'Sheet to print'}
              </button>
            )}
            {canManage && !adding && (
              <button className="gold small" onClick={() => setAdding(true)}>
                Make some
              </button>
            )}
          </div>
        }
      >
        {note && <div className="notice">{note}</div>}
        <ErrorBanner error={q.error ?? create.error ?? retire.error} />

        <p className="muted small">
          A member scans one and keeps their own card as they play. The code names the
          PLACE and never a person — nobody is signed in by scanning, so a photograph
          of a sticker is worth nothing, and one printed in March is still right in
          October.
        </p>

        {adding && (
          <form className="form-rows" style={{ maxWidth: 560 }}
            onSubmit={(e) => { e.preventDefault(); create.run(); }}>
            <div className="grid-2">
              <label className="field">
                <span>Label</span>
                <input required value={f.label} placeholder="Cart"
                  onChange={(e) => setF({ ...f, label: e.target.value })} />
                <div className="hint">
                  More than one gets numbered — Cart 1, Cart 2 — so a sticker can be
                  matched back to the cart it is on.
                </div>
              </label>
              <label className="field">
                <span>How many</span>
                <input type="number" min={1} max={60} value={f.count}
                  onChange={(e) => setF({ ...f, count: e.target.value })} />
              </label>
              <label className="field">
                <span>Rounds started here begin on hole</span>
                <input type="number" min={1} max={18} value={f.starting_hole}
                  onChange={(e) => setF({ ...f, starting_hole: e.target.value })} />
                <div className="hint">
                  1 for a cart or the first tee. 10 for the tenth-tee sticker, which is
                  how a shotgun start knows where somebody began.
                </div>
              </label>
            </div>
            <div className="actions">
              <button className="gold" type="submit" disabled={create.busy}>Make them</button>
              <button className="secondary" type="button"
                onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </form>
        )}

        {q.loading && rows.length === 0 ? <Loading /> : rows.length === 0 ? (
          <Empty>
            No stickers yet. Without one a member can still start a card from this app —
            the sticker is what makes it one tap from standing on the tee.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Where</th><th>Code</th>
                <th className="num">Starts on</th><th className="num">Scans</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.scan_point_id}>
                  <td>
                    {s.label}
                    <div className="hint">{s.course_name}</div>
                  </td>
                  <td className="mono">{s.code}</td>
                  <td className="num">{s.starting_hole}</td>
                  <td className="num">
                    {s.scans}
                    {/* A sticker never scanned has probably fallen off. */}
                    {s.last_scan_at
                      ? <div className="hint">{fmtDate(s.last_scan_at)}</div>
                      : <div className="hint">never</div>}
                  </td>
                  <td className="actions">
                    {!s.is_active && <Status tone="neutral">Retired</Status>}
                    {canManage && (
                      <button className="secondary small" disabled={retire.busy}
                        onClick={() => retire.run({
                          id: s.scan_point_id, active: !s.is_active,
                        })}>
                        {s.is_active ? 'Retire it' : 'Put it back'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {sheet && rows.length > 0 && (
        <Card
          title="Sheet to Print"
          actions={
            <button className="secondary small" onClick={() => window.print()}>
              Print
            </button>
          }
        >
          <p className="muted small no-print">
            Cut along the squares. The code is printed under each one because a faded
            sticker gets read out over the phone — there is no 0, 1, I, l, O or u in
            any of them for that reason.
          </p>
          <div className="stickers">
            {rows.filter((s) => s.is_active).map((s) => (
              <div className="sticker" key={s.scan_point_id}>
                <QR text={`${origin}/s/${s.code}`} />
                <div className="sticker__label">{s.label}</div>
                <div className="sticker__code mono">{s.code}</div>
                {s.starting_hole !== 1 && (
                  <div className="sticker__hole">Starts on {s.starting_hole}</div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}
